import { FC, useRef } from "react";
import BarChart from "./BarChart";

import { ChartToolTip, ChartToolTipBody } from "../ChartTooltip";
import { NonOverflowTooltip } from "./NonOverflowTooltip";

import { BarDatum, BarTooltipProps } from "@nivo/bar";
import styled from "styled-components";
import { formatUnits } from "../../formatting/formatKiloUnits";
import TabButtons from "../buttons/TabButtons";
import { Column } from "../layout/Flex";
import { ChartData, getChartLegend, legendKeyColors } from "./chart-types";
import {
  ChartHeader,
  ChartTooltipColorBox,
  ResponsiveChartHeightChart,
  ResponsiveChartHeightChartHeaderWithTabs,
  ResponsiveChartHeightChartLegend,
  ResponsiveChartHeightChartWrapper,
  ResponsiveChartHeightRelativeWrapper,
} from "./common";

interface YearDayEnergyChartProps {
  header?: string;
  chartData: ChartData[];
  energyProcuredChartPeriod: "year" | "summer" | "winter";
  setEnergyProcuredChartPeriod: (period: "year" | "summer" | "winter") => void;
  height?: string;
  flexGrow?: string;
  chartHeight?: string;
  isNotYetProcuringSite?: boolean;
  buyEnergyBlock?: React.ReactNode;
  $padding?: string;
  tooltipFontSize?: string;
  chartMargins?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  stacked?: boolean;
}

const YearDayEnergyChart: FC<YearDayEnergyChartProps> = ({
  header,
  chartData,
  energyProcuredChartPeriod, // ["year", "summer", "winter"]
  setEnergyProcuredChartPeriod,
  height,
  flexGrow,
  chartHeight,
  isNotYetProcuringSite,
  buyEnergyBlock,
  $padding,
  tooltipFontSize,
  chartMargins,
  stacked,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const chartLegend = getChartLegend(chartData, isNotYetProcuringSite);

  const ToolTipData: FC<{ data: BarDatum }> = ({ data }) => {
    return (
      <>
        {Object.keys(chartLegend).map((key, index) => (
          <ChartToolTipBody key={index} $fontSize={tooltipFontSize}>
            <ChartTooltipColorBox color={legendKeyColors[key]} />
            {key}:{" "}
            <strong>
              {formatUnits(
                typeof data[key] === "string"
                  ? parseFloat(data[key])
                  : data[key],
                "kWh"
              )}
            </strong>
          </ChartToolTipBody>
        ))}
      </>
    );
  };

  const showToolTipFn = (e: BarTooltipProps<BarDatum>) => {
    const eventCoords = e as unknown as { x: number; y: number };
    return (
      <NonOverflowTooltip
        point={{ x: eventCoords.x, y: eventCoords.y }}
        containerSize={ref.current!.getBoundingClientRect()}
      >
        <ChartToolTip>
          <ToolTipData data={e.data} />
        </ChartToolTip>
      </NonOverflowTooltip>
    );
  };

  return (
    <ResponsiveChartHeightChartWrapper
      ref={ref}
      height={height}
      $flexGrow={flexGrow}
      $padding={$padding}
    >
      <ResponsiveChartHeightChartHeaderWithTabs>
        <div>
          {header && (
            <>
              <ChartHeader>{header}</ChartHeader>
            </>
          )}
        </div>
        <TabButtons
          tab1={{
            label: "Year 1",
            active: energyProcuredChartPeriod === "year",
            onClick: () => setEnergyProcuredChartPeriod("year"),
          }}
          tab2={{
            label: "Daily (Summer)",
            active: energyProcuredChartPeriod === "summer",
            onClick: () => setEnergyProcuredChartPeriod("summer"),
          }}
          tab3={{
            label: "Daily (Winter)",
            active: energyProcuredChartPeriod === "winter",
            onClick: () => setEnergyProcuredChartPeriod("winter"),
          }}
        />
      </ResponsiveChartHeightChartHeaderWithTabs>
      {Object.keys(chartLegend).length > 1 && (
        <ResponsiveChartHeightChartLegend
          chartLegend={Object.keys(chartLegend).map((key) => ({
            label: key,
            color: chartLegend[key],
          }))}
        />
      )}
      <ResponsiveChartHeightRelativeWrapper>
        <ResponsiveChartHeightChart $height={chartHeight}>
          <BarChart
            chartData={chartData as BarDatum[]}
            barColors={Object.values(chartLegend)}
            tooltipFn={showToolTipFn}
            keys={Object.keys(chartLegend)}
            legend={"kWh"}
            stacked={stacked === undefined ? true : stacked}
            tickRotationProp={energyProcuredChartPeriod === "year" ? 0 : 90}
            chartMargins={chartMargins}
          />
          {isNotYetProcuringSite && (
            <NonGeneratingSiteOverlay>
              <Column style={{ maxWidth: "570px" }}>
                <span
                  style={{
                    fontSize: "30px",
                    color: "#757575",
                    margin: "0 15px 15px 15px",
                    padding: "0 8px",
                  }}
                >
                  Your site is not yet procuring energy.
                </span>
                {buyEnergyBlock}
              </Column>
            </NonGeneratingSiteOverlay>
          )}
        </ResponsiveChartHeightChart>
      </ResponsiveChartHeightRelativeWrapper>
    </ResponsiveChartHeightChartWrapper>
  );
};

// -54px top to account for the chart legend
const NonGeneratingSiteOverlay = styled(Column)`
  position: absolute;
  top: -54px;
  left: 15%;
  padding-left: 12px;
  width: 70%;
  height: 100%;
  background-color: transparent;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export default YearDayEnergyChart;
